import React from 'react'
import {
  ComponentConfig,
  ComponentData,
  Slot,
  SlotComponent,
  usePuck,
  createUsePuck,
} from '@puckeditor/core'
import { ArrowRight, TriangleAlert } from 'lucide-react'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import { cleanRichtext, richtextField } from '../ReferralCard/ReferralCard'
import {
  PQ_DISALLOW,
  SlotHint,
  pqResultColor,
  pqResultLabel,
} from '../PQResult/PQResult'
import { PQResultPicker } from './PQResultPicker'

export type PQSkipLogicRow = { key: string; value: number }

export type PQAnswerProps = {
  id?: string
  /** Answer copy. Empty text + an image in the media slot = image-only
   * answer (legacy hideText — derived, not a field). */
  text?: string
  /** Single Result mode: the one result this answer votes for. */
  resultId?: string
  /** Decision Tree mode: the set of results this answer votes for. */
  resultIds?: string[]
  /** Decision Tree mode: attribute weights accumulated per answer (legacy
   * keyValuePairs; question gates consume the accumulated scores). */
  skipLogic?: PQSkipLogicRow[]
  bgColor?: string
  textColor?: string
  /** Optional answer image — presence makes this an IMAGE-type answer. */
  media: Slot
}

export const PQ_ANSWER_DEFAULT_PROPS: PQAnswerProps = {
  text: 'Answer',
  media: [],
}

/** Reads the container's Result Display mode from the current data. */
export const isDecisionTree = (data: { content?: ComponentData[] }): boolean =>
  ((data.content ?? []).find(c => c.type === 'PQContainer')?.props as
    | { resultDisplay?: string }
    | undefined)?.resultDisplay === 'DECISION_TREE'

/** Legacy keyValuePairs object from skip-logic rows (undefined when empty so
 * quiz-utils' `if (x.keyValuePairs)` guards short-circuit). */
export const skipLogicToKeyValuePairs = (
  rows?: PQSkipLogicRow[],
): Record<string, number> | undefined => {
  const valid = (rows ?? []).filter(r => r.key?.trim())
  if (!valid.length) return undefined
  return Object.fromEntries(valid.map(r => [r.key.trim(), Number(r.value) || 0]))
}

/** Skip logic is beaker's decision-tree branching: answers add points to
 * named attributes; questions are hidden once a picked attribute's running
 * total passes a threshold. Advanced/optional — most quizzes leave it empty. */
const skipLogicField = (label: string, keyLabel: string, valueLabel: string) => ({
  type: 'array' as const,
  label,
  getItemSummary: (item: PQSkipLogicRow, i?: number) =>
    item?.key ? `${item.key} = ${item.value}` : `Rule ${(i ?? 0) + 1}`,
  arrayFields: {
    key: { type: 'text' as const, label: keyLabel },
    value: { type: 'number' as const, label: valueLabel },
  },
  defaultItemProps: { key: '', value: 1 },
})

// Answers ADD points to an attribute; questions HIDE when a total passes.
export const answerSkipLogicField = skipLogicField(
  'Branching points (advanced) — add points to an attribute when this answer is picked',
  'Attribute name (e.g. "sweet")',
  'Points to add',
)
export const questionSkipLogicField = skipLogicField(
  'Branching gates (advanced) — hide this question when an attribute total goes over the value',
  'Attribute name to check',
  'Max allowed total (hidden when exceeded)',
)

/** Options for the answer→result select, read from the sibling results slot
 * of the PQContainer in the current data. */
const resultOptions = (data: {
  content?: ComponentData[]
}): { label: string; value: string }[] => {
  const container = (data.content ?? []).find(c => c.type === 'PQContainer')
  const results = ((container?.props as { results?: ComponentData[] })
    ?.results ?? []) as ComponentData[]
  return [
    { label: '— pick a result —', value: '' },
    ...results.map((r, i) => ({
      label: pqResultLabel(r.props as { title?: string }, i),
      value: String(r.props?.id ?? ''),
    })),
  ]
}

/** Result options with identity colors, for the decision-tree picker. */
const resultPickerOptions = (data: { content?: ComponentData[] }) => {
  const container = (data.content ?? []).find(c => c.type === 'PQContainer')
  const results = ((container?.props as { results?: ComponentData[] })
    ?.results ?? []) as ComponentData[]
  return results.map((r, i) => ({
    value: String(r.props?.id ?? ''),
    label: pqResultLabel(r.props as { title?: string }, i),
    color: pqResultColor(i),
  }))
}

const usePuckSel = createUsePuck()

/** Resolve the currently-edited answer's id from the selection (the fields
 * panel showing this field belongs to the selected answer). */
const selectedAnswerId = (
  data: { content?: ComponentData[] },
  sel?: { index: number; zone?: string } | null,
): string | undefined => {
  if (!sel?.zone) return undefined
  const parentId = sel.zone.split(':')[0]
  const container = (data.content ?? []).find(c => c.type === 'PQContainer')
  const questions = ((container?.props as { questions?: ComponentData[] })
    ?.questions ?? []) as ComponentData[]
  const q = questions.find(x => x.props?.id === parentId)
  const answers = ((q?.props as { answers?: ComponentData[] })?.answers ??
    []) as ComponentData[]
  return answers[sel.index]?.props?.id as string | undefined
}

/**
 * Stable render for the resultIds field: reads data via usePuck (so the
 * field object identity stays constant across keystrokes — an inline render
 * remounts the whole panel and jumps its scroll).
 */
const ResultIdsField = ({
  value,
  onChange,
}: {
  value?: string[]
  onChange: (v: string[]) => void
}) => {
  const data = usePuckSel(s => s.appState.data)
  const itemSelector = usePuckSel(s => s.appState.ui.itemSelector)
  return (
    <PQResultPicker
      options={resultPickerOptions(data)}
      value={value}
      onChange={onChange}
      selfId={selectedAnswerId(data, itemSelector)}
    />
  )
}

const resultIdsField = {
  type: 'custom' as const,
  label: 'Leads to Results (decision tree)',
  render: ({
    value,
    onChange,
  }: {
    value?: string[]
    onChange: (v: string[]) => void
  }) => <ResultIdsField value={value} onChange={onChange} />,
}

/**
 * One quiz answer. `resultId` points at a sibling PQResult (options resolved
 * live from the container's results slot — add results first, then wire
 * answers). Production maps to the legacy answer shape; the tally logic in
 * quiz-utils consumes `resultId` unchanged.
 */
export const PuckPQAnswer: ComponentConfig<{
  props: PQAnswerProps
  fields: ThemeCustomFields
}> = {
  label: 'Personality Quiz Answer',
  defaultProps: PQ_ANSWER_DEFAULT_PROPS,
  fields: {
    text: richtextField('Answer Text'),
    resultId: {
      type: 'select',
      label: 'Leads to Result',
      options: [{ label: '— pick a result —', value: '' }],
    },
    bgColor: {
      type: 'colorPicker',
      label: 'Background Color',
      clearable: true,
    },
    textColor: { type: 'colorPicker', label: 'Text Color', clearable: true },
    media: {
      type: 'slot',
      label: 'Answer Image',
      disallow: PQ_DISALLOW,
    },
  },
  resolveFields: (_data, { appState, fields }) => {
    const data =
      (appState as { data?: { content?: ComponentData[] } }).data ?? {}
    const options = resultOptions(data)
    const {
      resultId: _r,
      resultIds: _rs,
      skipLogic: _sl,
      ...rest
    } = fields

    if (isDecisionTree(data)) {
      return {
        ...rest,
        resultIds: resultIdsField,
        skipLogic: answerSkipLogicField,
      } as typeof fields
    }

    return {
      ...rest,
      resultId: { type: 'select', label: 'Leads to Result', options },
    } as typeof fields
  },
  render: props => <PQAnswerRender {...props} />,
}

type PQAnswerRenderProps = Omit<PQAnswerProps, 'media'> & {
  media: SlotComponent
}

/** Editor-only render — legacy answer <li> markup inside the question list. */
function PQAnswerRender(props: PQAnswerRenderProps) {
  const { id, text, resultId, bgColor, textColor, media: Media } = props

  // Container defaults + stored props (media emptiness, result title) come
  // from the stored data, same appState pattern as ReferralCard.
  const puck = usePuck()
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'PQContainer')
  const containerProps = container?.props as
    | {
        defaultAnswerBgColor?: string
        defaultAnswerTextColor?: string
        questions?: ComponentData[]
        results?: ComponentData[]
      }
    | undefined

  // this answer's stored props + its parent question (media hint, card font)
  let storedAnswer: ComponentData | undefined
  let parentQuestion: ComponentData | undefined
  for (const q of containerProps?.questions ?? []) {
    const hit = (
      ((q.props as { answers?: ComponentData[] })?.answers ?? [])
    ).find(a => a.props?.id === id)
    if (hit) {
      storedAnswer = hit
      parentQuestion = q
      break
    }
  }
  const mediaEmpty = !(
    (storedAnswer?.props as { media?: ComponentData[] })?.media?.length
  )
  const questionFont = parentQuestion?.props as
    | { fontFamily?: string; fontFamilyFallback?: string }
    | undefined

  const results = (containerProps?.results ?? []) as ComponentData[]
  const tree =
    (containerProps as { resultDisplay?: string } | undefined)
      ?.resultDisplay === 'DECISION_TREE'
  const linkedIds = tree ? props.resultIds ?? [] : resultId ? [resultId] : []
  const targets = linkedIds
    .map(rid => {
      const idx = results.findIndex(r => r.props?.id === rid)
      return idx >= 0
        ? {
            label: pqResultLabel(
              results[idx].props as { title?: string },
              idx,
            ),
            color: pqResultColor(idx),
          }
        : null
    })
    .filter((t): t is { label: string; color: string } => !!t)

  const textHtml = cleanRichtext(text)

  return (
    <li
      className="pointer"
      style={{ listStyle: 'none', width: '100%' }}
      data-pq-answer-id={id}
    >
      {textHtml && (
        <label
          className="custom-btn-label"
          style={{
            display: 'block',
            width: '100%',
            backgroundColor:
              bgColor || containerProps?.defaultAnswerBgColor || undefined,
            color:
              textColor || containerProps?.defaultAnswerTextColor || undefined,
            // question card font inline — the quiz scss sets an explicit
            // family on answer labels, inheritance loses
            ...(questionFont?.fontFamily
              ? {
                  fontFamily: questionFont.fontFamilyFallback
                    ? `${questionFont.fontFamily}, ${questionFont.fontFamilyFallback}`
                    : questionFont.fontFamily,
                }
              : {}),
          }}
        >
          <input type="radio" disabled readOnly checked={false} />
          <span dangerouslySetInnerHTML={{ __html: textHtml }} />
        </label>
      )}
      {/* editor-only sizing — full-width images made the stack scroll
          forever; production sizes answers via the question's Answer Width.
          Only shown when this answer actually has (or is meant to have) an
          image, so text answers don't carry a dead drop box. */}
      <div className="image-choice" style={{ width: '100%' }}>
        <SlotHint
          hint="Answer image (optional)"
          isEmpty={mediaEmpty}
        >
          <Media disallow={PQ_DISALLOW} minEmptyHeight={mediaEmpty ? 40 : 0} />
        </SlotHint>
      </div>
      {/* answer→result link chips: colors match the result cards' bands */}
      <span
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          width: '100%',
          marginTop: 4,
        }}
      >
        {targets.length ? (
          targets.map(t => (
            <span
              key={t.label + t.color}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 4,
                color: t.color,
                // alpha tint of the identity color (readable on dark bgs)
                background: `${t.color}26`,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: t.color,
                  flex: '0 0 auto',
                }}
              />
              <ArrowRight size={12} style={{ flex: '0 0 auto' }} />
              {t.label}
            </span>
          ))
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: 4,
              color: '#c0392b',
              background: 'rgba(192,57,43,0.12)',
            }}
          >
            <TriangleAlert size={12} style={{ flex: '0 0 auto' }} />
            {tree ? 'no results linked' : 'no result linked'}
          </span>
        )}
      </span>
    </li>
  )
}
